// Subscriptions — TanStack Query hooks.
// Query keys включают tenantId (см. docs/stack/07-frontend-vite.md §4) + фильтры.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useCan } from '@/lib/auth-context';
import { useTenantId } from '@/lib/tenant-context';

import {
  createTemplate,
  deleteTemplate,
  getSubscriptionsStats,
  listSubscriptions,
  listSubscriptionTemplateOptions,
  listTemplates,
  updateTemplate,
  type CreateTemplateBody,
  type ListSubscriptionsParams,
  type SubscriptionListItem,
  type SubscriptionsStats,
  type SubscriptionTemplate,
  type SubscriptionTemplateOption,
  type UpdateTemplateBody,
} from '../api/subscriptions.api';

export interface SubscriptionsFilters {
  clientId?: string;
  status?: ListSubscriptionsParams['status'];
  branchId?: string;
  search?: string;
  templateType?: ListSubscriptionsParams['templateType'];
  templateId?: string;
  limit?: number;
}

export function subscriptionsKeys(tenantId: string | null) {
  const root = ['subscriptions', tenantId] as const;
  return {
    root,
    templates: [...root, 'templates'] as const,
    templatesWith: (includeArchived: boolean) =>
      [...root, 'templates', { includeArchived }] as const,
    list: (filters: SubscriptionsFilters) => [...root, 'list', filters] as const,
    stats: (branchId?: string) => [...root, 'stats', branchId ?? null] as const,
    templateOptions: (filters: {
      status?: ListSubscriptionsParams['status'];
      branchId?: string;
      search?: string;
    }) => [...root, 'template-options', filters] as const,
  };
}

/**
 * Data-driven варианты фильтра «Абонемент» в «Проданных»: только шаблоны,
 * у которых есть проданные абонементы под текущие фильтры (статус/филиал/поиск).
 */
export function useSubscriptionTemplateOptions(filters: {
  status?: ListSubscriptionsParams['status'];
  branchIds?: string[];
  search?: string;
}): UseQueryResult<SubscriptionTemplateOption[], Error> {
  const tenantId = useTenantId();
  const keys = subscriptionsKeys(tenantId);
  // Ф1 плана 38: без product:read сервер даёт 403 — не дёргаем API вовсе,
  // чтобы у роли-без-права не сыпались ошибки в «Логи» (и не мигал UI).
  const canRead = useCan()('product:read');
  return useQuery({
    queryKey: keys.templateOptions(filters),
    queryFn: () => listSubscriptionTemplateOptions(filters),
    staleTime: 30_000,
    enabled: canRead,
  });
}

export function useTemplates(
  includeArchived = false,
): UseQueryResult<SubscriptionTemplate[], Error> {
  const tenantId = useTenantId();
  const keys = subscriptionsKeys(tenantId);
  const canRead = useCan()('product:read');

  return useQuery({
    queryKey: keys.templatesWith(includeArchived),
    queryFn: async () => (await listTemplates(includeArchived)).data,
    staleTime: 5 * 60_000, // справочник меняется нечасто.
    enabled: canRead,
  });
}

export function useSubscriptions(
  filters: SubscriptionsFilters = {},
): UseQueryResult<SubscriptionListItem[], Error> {
  const tenantId = useTenantId();
  const keys = subscriptionsKeys(tenantId);
  const canRead = useCan()('product:read');

  return useQuery({
    queryKey: keys.list(filters),
    enabled: canRead,
    queryFn: async () =>
      (
        await listSubscriptions({
          ...(filters.clientId ? { clientId: filters.clientId } : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.branchId ? { branchId: filters.branchId } : {}),
          ...(filters.search ? { search: filters.search } : {}),
          ...(filters.templateType ? { templateType: filters.templateType } : {}),
          ...(filters.templateId ? { templateId: filters.templateId } : {}),
          // Лимит просим явно (батч №23 П5): по умолчанию сервер отдаёт 50, и
          // карточка клиента фильтровала «Архив»/«Все» поверх этой полусотни, а
          // не поверх всей истории абонементов. 100 — максимум, который
          // принимает DTO.
          limit: filters.limit ?? 100,
        })
      ).data,
  });
}

export function useSubscriptionsStats(
  branchId?: string,
): UseQueryResult<SubscriptionsStats, Error> {
  const tenantId = useTenantId();
  const keys = subscriptionsKeys(tenantId);
  return useQuery({
    queryKey: keys.stats(branchId),
    queryFn: () => getSubscriptionsStats(branchId),
    staleTime: 60_000,
  });
}

export function useCreateTemplate(): UseMutationResult<
  SubscriptionTemplate,
  Error,
  CreateTemplateBody
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = subscriptionsKeys(tenantId);

  return useMutation({
    mutationFn: (body: CreateTemplateBody) => createTemplate(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useUpdateTemplate(): UseMutationResult<
  SubscriptionTemplate,
  Error,
  { id: string; body: UpdateTemplateBody }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = subscriptionsKeys(tenantId);

  return useMutation({
    mutationFn: ({ id, body }) => updateTemplate(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useDeleteTemplate(): UseMutationResult<void, Error, string> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = subscriptionsKeys(tenantId);

  return useMutation({
    mutationFn: (id: string) => deleteTemplate(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}
